import { useQuery } from '@tanstack/react-query';
import { type ReactNode, useState } from 'react';

import { useProject } from '../projects/context';
import { useProjectApi } from '../projects/useProjectApi';
import { noteParts } from './callNoteParts';

// CallNote: KI-Mitschrift eines Gesprächs in der Historie – auf Klick aus der Auswertung geladen (Aufbewahrungsfrist
// der KI-Ausgaben gilt; danach „nicht mehr verfügbar“).
export function CallNote({ callId }: { callId: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="call-note">
      <button type="button" className="btn btn-ghost btn-small" aria-expanded={open} onClick={() => setOpen(!open)}>
        {open ? 'Mitschrift ausblenden' : 'Mitschrift anzeigen'}
      </button>
      {open && <CallNoteBody callId={callId} />}
    </div>
  );
}

function CallNoteBody({ callId }: { callId: string }) {
  const { project } = useProject();
  const papi = useProjectApi();
  const q = useQuery({ queryKey: ['projects', project.id, 'analysis', callId], queryFn: ({ signal }) => papi.callAnalysis(callId, signal) });
  if (q.isPending) return <p className="muted small">Mitschrift wird geladen …</p>;
  const a = q.data?.analysis;
  if (q.isError || !a || a.status !== 'COMPLETED') return <p className="muted small">Mitschrift nicht verfügbar.</p>;
  const n = noteParts(a.output, a.summary);
  if (!n.summary) return <p className="muted small">Mitschrift nicht mehr verfügbar (Aufbewahrungsfrist).</p>;
  const facts: [string, ReactNode][] = [];
  if (n.statements.length > 0) {
    // Zeilen statt Liste: Die Historie gestaltet ihre eigenen Listeneinträge (.timeline li).
    facts.push([
      'Wichtige Aussagen',
      <span key="s" className="call-note-lines">
        {n.statements.map((s) => (
          <span key={s}>{s}</span>
        ))}
      </span>,
    ]);
  }
  if (n.callback) facts.push(['Rückruf', n.callback]);
  if (n.nextStep) facts.push(['Nächster Schritt', n.nextStep]);
  if (n.objections.length > 0) facts.push(['Einwände', n.objections.join(' · ')]);
  for (const [label, value] of n.property) facts.push([label, value]);
  if (n.openQuestions.length > 0) facts.push(['Offene Fragen', n.openQuestions.join(' · ')]);
  return (
    <div className="call-note-body" role="region" aria-label="KI-Mitschrift">
      <p>{n.summary}</p>
      {facts.length > 0 && (
        <dl className="call-note-facts">
          {facts.map(([label, value]) => (
            <div key={label}>
              <dt>{label}</dt>
              <dd>{value}</dd>
            </div>
          ))}
        </dl>
      )}
      <p className="muted small">KI-Mitschrift – bitte prüfen. Ohne Zustimmung des Kunden nur aus der Gesprächsseite des Mitarbeiters abgeleitet.</p>
    </div>
  );
}
